import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Create axios instance with auth interceptor
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Add auth token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface CallTask {
  id: string;
  status: 'pending' | 'processing' | 'completed';
  gcsBlobName: string;
  clientEmail: string;
  clientName?: string;
  phoneNumber: string;
  userFirstname?: string;
  userLastname?: string;
  userExtension?: string;
  callDatetime?: string;
  callId?: string;
  transcription?: string;
  durationSeconds?: number;
  fileSize?: number;
  createdAt: string;
  completedAt?: string;
  completedBy?: string;
  claimId?: string;
  assignedClaimIds?: string[];
  expectedClaimsCount?: number;
  notes?: string;
  errorMessage?: string;
}

export interface CallTaskClaim {
  id: string;
  CRAClientName: string;
  email: string;
  status?: string;
  createdInApiAt?: string;
}

export interface CallTaskStats {
  totalTasks: number;
  pendingTasks: number;
  completedTasks: number;
  completedToday: number;
}

export interface ProcessorStatus {
  enabled: boolean;
  intervalMinutes: number;
  taskStats: CallTaskStats;
  unprocessedFilesCount: number;
  nextRunTime?: string;
}

export interface ProcessorResults {
  totalFiles: number;
  matchedClients: number;
  noClientMatch: number;
  tasksCreated: number;
  filesMoved: number;
  transcriptionErrors: number;
  otherErrors: number;
  errors: Array<{ file: string; error: string }>;
}

export const callTasksApi = {
  // Get call tasks with filters
  getTasks: async (filters?: {
    status?: string;
    clientEmail?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ success: boolean; tasks: CallTask[]; count: number; filters: any }> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.clientEmail) params.append('client_email', filters.clientEmail);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await axiosInstance.get(`/api/call-tasks?${params.toString()}`);
    return response.data as { success: boolean; tasks: CallTask[]; count: number; filters: any };
  },

  // Get a specific task by ID
  getTask: async (taskId: string): Promise<{ success: boolean; task: CallTask }> => {
    const response = await axiosInstance.get(`/api/call-tasks/${taskId}`);
    return response.data as { success: boolean; task: CallTask };
  },

  // Get claims for a task's client
  getTaskClaims: async (taskId: string): Promise<{
    success: boolean;
    claims: CallTaskClaim[];
    taskId: string;
    clientEmail: string;
    count: number;
  }> => {
    const response = await axiosInstance.get(`/api/call-tasks/${taskId}/claims`);
    return response.data as {
      success: boolean;
      claims: CallTaskClaim[];
      taskId: string;
      clientEmail: string;
      count: number;
    };
  },

  // Complete a task
  completeTask: async (taskId: string, data: {
    claim_id: string;
    notes?: string;
    call_answered?: boolean;
    direction?: 'incoming' | 'outgoing';
    expected_claims_count?: number;
  }): Promise<{
    success: boolean;
    task: CallTask;
    correspondenceId: string;
    claimId: string;
    unitsAdded: number;
    message: string;
  }> => {
    const response = await axiosInstance.post(`/api/call-tasks/${taskId}/complete`, data);
    return response.data as {
      success: boolean;
      task: CallTask;
      correspondenceId: string;
      claimId: string;
      unitsAdded: number;
      message: string;
    };
  },

  // Complete a task for multiple claims
  completeTaskForAllClaims: async (taskId: string, data: {
    claim_ids: string[];
    notes?: string;
    call_answered?: boolean;
    direction?: 'incoming' | 'outgoing';
  }): Promise<{
    success: boolean;
    results: Array<{
      claimId: string;
      success: boolean;
      correspondenceId?: string;
      error?: string;
    }>;
    message: string;
  }> => {
    const response = await axiosInstance.post(`/api/call-tasks/${taskId}/complete-batch`, data);
    return response.data as {
      success: boolean;
      results: Array<{
        claimId: string;
        success: boolean;
        correspondenceId?: string;
        error?: string;
      }>;
      message: string;
    };
  },

  // Delete a task (admin only)
  deleteTask: async (taskId: string): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.delete(`/api/call-tasks/${taskId}`);
    return response.data as { success: boolean; message: string };
  },

  // Get task statistics
  getStats: async (): Promise<{ success: boolean; stats: CallTaskStats }> => {
    const response = await axiosInstance.get('/api/call-tasks/stats');
    return response.data as { success: boolean; stats: CallTaskStats };
  },

  // Admin: Get processor status
  getProcessorStatus: async (): Promise<ProcessorStatus> => {
    const response = await axiosInstance.get('/ui/admin/call-tasks-processor');
    return response.data as ProcessorStatus;
  },

  // Admin: Enable processor
  enableProcessor: async (): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.post('/ui/admin/call-tasks-processor/enable');
    return response.data as { success: boolean; message: string };
  },

  // Admin: Disable processor
  disableProcessor: async (): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.post('/ui/admin/call-tasks-processor/disable');
    return response.data as { success: boolean; message: string };
  },

  // Admin: Manually trigger processor
  runProcessor: async (): Promise<{ success: boolean; message: string; results: ProcessorResults }> => {
    const response = await axiosInstance.post('/ui/admin/call-tasks-processor/run');
    return response.data as { success: boolean; message: string; results: ProcessorResults };
  },

  // Admin: Process one file (for testing)
  processOneFile: async (): Promise<{
    success: boolean;
    message: string;
    result: any
  }> => {
    const response = await axiosInstance.post('/ui/admin/call-tasks-processor/process-one');
    return response.data as { success: boolean; message: string; result: any };
  },
};
